import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { InvoiceFileUpload } from "~/components/invoice-file-upload";
import { cn } from "~/components/ui/cn";
import { Form, FormControl, FormField, FormItem } from "~/components/ui/form";
import { toast } from "~/components/ui/toast";
import type { InvoicesQuery } from "~/graphql/graphql-new.gen";
import { InvoiceStatus, InvoiceType } from "~/graphql/graphql-new.gen";
import { useDownPaymentUploadMutation } from "~/graphql/hooks";
import { useInvoiceCreatedSubscription } from "~/hooks/use-invoice-created-subscription";
import { useInvoiceUpdatedSubscription } from "~/hooks/use-invoice-updated-subscription";
import { amDefaultContact, findContact } from "~/stores/choose-contact";
import { activeSaleState } from "~/stores/sales";
import { formatPrice } from "~/utils/currency";
import { formatPercentage } from "~/utils/number";

import { AmContact } from "./am-contact";
import { useBilling } from "./use-billing";
import { useInvoices } from "./use-invoices";

type InvoiceFormData = {
  file: File;
};

type DownPaymentUploadProps = {
  isSubjectVat: boolean;
};

export const DownPaymentUpload: React.FC<DownPaymentUploadProps> = ({
  isSubjectVat,
}) => {
  const { data: billing } = useBilling();
  const { data: invoices } = useInvoices();
  const { i18n, t } = useTranslation();
  const queryClient = useQueryClient();

  const activeSale = useAtomValue(activeSaleState);
  const amContact = findContact(
    activeSale?.logistics_manager ?? undefined,
    amDefaultContact,
  );

  const form = useForm<InvoiceFormData>({
    mode: "onTouched",
  });
  const [downPaymentUploadMutation, { loading, progress }] =
    useDownPaymentUploadMutation();

  useInvoiceCreatedSubscription();
  useInvoiceUpdatedSubscription();

  const onSubmit = async (data: InvoiceFormData) => {
    try {
      await downPaymentUploadMutation({
        file: data.file,
        saleId: activeSale?.id ?? "",
      });

      toast.success(t("invoice.down_payment_upload.upload_success"));

      form.reset();

      queryClient.setQueryData(
        ["invoices", { saleId: activeSale?.id ?? "" }],
        (oldData: InvoicesQuery) => ({
          invoices: [
            ...(oldData?.invoices ?? []),
            {
              __typename: "Invoice",
              createdAt: new Date().toISOString(),
              fileName: data.file.name,
              fileUrl: "",
              id: self.crypto.randomUUID(),
              status: InvoiceStatus.BotProcessing,
            },
          ],
        }),
      );
    } catch {
      toast.error(t("invoice.down_payment_upload.upload_error"));
      form.reset();
    }
  };

  if (
    !billing ||
    billing.down_payment?.__typename !== "DownPaymentClassic" ||
    (billing.down_payment?.__typename === "DownPaymentClassic" &&
      billing.down_payment.rules.some((rule) => !rule.isValidated)) ||
    invoices
      ?.filter((invoice) => invoice.type === InvoiceType.DownPayment)
      .some(
        (invoice) =>
          invoice.status === InvoiceStatus.Validated ||
          invoice.status === InvoiceStatus.ToBePaid ||
          invoice.status === InvoiceStatus.Paid,
      )
  ) {
    return null;
  }

  const remainingAmount = isSubjectVat
    ? billing.down_payment.financialDetail.total_remaining_ttc
    : billing.down_payment.financialDetail.total_remaining_ht;
  const hasInvoices = (invoices?.length ?? 0) > 0;

  form.watch("file");

  return (
    <section className="flex flex-col gap-4 rounded-lg border border-gray-100 p-6">
      <div
        className={cn({
          "opacity-50": loading,
        })}
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:gap-6">
          <h2 className="text-lg font-bold text-gray-900">
            {t("invoice.down_payment_upload.import_a_down_payment")}
          </h2>
          <p className="shrink-0 text-3.5xl font-bold text-gray-900 sm:text-right">
            {formatPrice(
              remainingAmount / 100,
              i18n.language === "fr" ? "fr" : "en",
              activeSale?.currency.toString().toUpperCase() ?? "EUR",
            )}
          </p>
        </div>
        <div className="text-sm text-gray-700 sm:text-right">
          {isSubjectVat
            ? t("invoice.down_payment_upload.part_of_total_be_invoiced_ttc", {
                amount: formatPercentage(
                  billing.down_payment.rate / 100,
                  i18n.language,
                ),
              })
            : t("invoice.down_payment_upload.part_of_total_be_invoiced_ht", {
                amount: formatPercentage(
                  billing.down_payment.rate / 100,
                  i18n.language,
                ),
              })}
        </div>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="file"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <InvoiceFileUpload
                    isUploading={loading}
                    progress={progress}
                    value={field.value}
                    onValueChange={(file) => {
                      field.onChange(file);
                      if (file) form.handleSubmit(onSubmit)();
                    }}
                    type="down_payment"
                    variant={hasInvoices ? "small" : "default"}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </form>
      </Form>
      <div className="border-t border-gray-100 pt-6">
        <AmContact contact={amContact} from="billing" horizontal />
      </div>
    </section>
  );
};
